import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { STORE_TIMEZONE } from './storeTime';
import { whenRange } from './whenRange';
import { inDays } from './inDays';
import { formatRun } from './bookingDurations';

dayjs.extend(utc);
dayjs.extend(timezone);

/** One pet's line on the booked page: "Leo · Bath & Blow-Dry, Nail Trim  $25.00". */
export interface BookedLine {
  petName: string;
  /** The services in booked order; one a package session pays for says so. */
  services: string;
  /** What the client pays for them: a package session is $0; after the line deals. */
  priceCents: number;
  /** The price before the line deals, struck beside `priceCents` (My booking only). */
  wasCents?: number;
  /** Each line's service deal as the server stored it (mpgadmin G138): its title, its dollars. */
  deals?: Array<{ title: string; cents: number }>;
  /**
   * A later pet's own price on a service (mpgadmin G154; MPP 11c, 11d): the
   * server's words, "2nd cat price", never "Custom price".
   */
  placeLabel?: string;
  /** What these services cost at the 1st pet's price, struck beside the place price; absent when unknown. */
  fullCents?: number;
  /** The travel line, its minutes Google's: the logo goes beside it (G43). */
  google?: boolean;
}

/**
 * A booking as the booked page draws it (SF 75/76), whichever way the client
 * came: from Review and book with no deposit, back from Stripe (the server's
 * payment summary), or read back on a refresh. Money in cents; times are UTC
 * instants.
 */
export interface BookedSummary {
  appointmentId: number;
  pets: { id?: number; name: string }[];
  startUtc: string;
  endUtc: string | null;
  /** "Any groomer", the groomer chosen, or null when this page doesn't know. */
  groomer: string | null;
  storeName: string;
  storeAddress: string | null;
  lines: BookedLine[];
  /**
   * Other amounts off the lines, before the promotion (My booking: a discount
   * the salon added, service savings). The booked page has none.
   */
  deductions?: { name: string; amountCents: number }[];
  /** A promotion taken off lines drawn at their full price (the review's own figures). */
  promotion: { name: string; amountCents: number } | null;
  /**
   * A deal for some ways to pay, off at the salon if paid its way (mpgadmin
   * D92, SF 99): never in the total; the row under "At the salon".
   */
  atSalon?: { label: string; amountCents: number } | null;
  totalCents: number;
  /** The deposit, owed or paid; 0 when there is none. */
  depositCents: number;
  depositPaid: boolean;
  /** What's left to pay at the salon. */
  balanceCents: number;
  /** The mobile the booking's text goes to, as the client reads it. */
  mobile: string | null;
  /** Who picks up, when it isn't the client (mpgadmin G105; SF 75, 76). */
  pickup?: { name: string | null; mobile: string } | null;
  /** The photo picked on Review and book didn't upload: the page asks for it again. */
  photoFailed?: boolean;
  /** A pick-up or home visit (SF 108, 113, 121): where, and who hands over. Null at the salon. */
  where?: BookedWhere | null;
}

/** Where a booking away happens, for the booked page (never a fee: that's a line). */
export interface BookedWhere {
  visitType: 1 | 2;
  /** "Unit 4, 12 Example St, Sampleton VIC 3000". */
  address: string;
  gettingIn?: string | null;
  /** Google's place ID, which "Open in Maps" goes by (mpgadmin G172: no position is kept). */
  placeId?: string | null;
  /** A pick-up brought back somewhere else (D117). */
  dropOff?: string | null;
  /** Who hands over on a pick-up, or who'll be home on a home visit, when it isn't the client. */
  handover?: { name: string; mobile: string } | null;
  /** Google's drive minutes, without traffic (G43); absent when unknown. */
  driveMinutes?: number | null;
  /** The minutes kept clear before and after (G42). */
  bufferMinutes?: number | null;
  /** A pick-up: "About 11:15 am · back home", the server's words for the time booked (SF 113; mpgadmin D117). */
  backWords?: string | null;
  /** A pick-up: about when we collect (UTC), before the groom's start (mpgadmin D117, 27 Oct 2026). Null when unknown. */
  collectUtc?: string | null;
  /** Before we come, as the address keeps it: "Outside tap", "Driveway". */
  siteChips?: string[];
}

/** "Open in Maps": Google's link for a place, which opens the maps app on a phone. */
export const mapsLink = (w: Pick<BookedWhere, 'address' | 'placeId'>): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(w.address)}${w.placeId ? `&query_place_id=${encodeURIComponent(w.placeId)}` : ''}`;

/** The booked card's place row: the salon, or (SF 108, 113) the client's address with how the groom happens. */
export const bookedPlace = (s: Pick<BookedSummary, 'storeName' | 'storeAddress' | 'where'>): { title: string; sub: string | null } =>
  s.where
    ? { title: s.where.visitType === 1 ? 'We collect from' : 'At your home', sub: s.where.address }
    : { title: s.storeName || 'The salon', sub: s.storeAddress };

export const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/** "2h 15m", "45m", "2h"; nothing for no run. */
export const durationLabel = (minutes: number) => (minutes > 0 ? formatRun(minutes) : '');

/** "Leo", "Leo & Milo", "Leo, Milo & Ziggy" (or "and" in a sentence). */
export const joinPets = (names: string[], and: '&' | 'and' = '&') => {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} ${and} ${list[list.length - 1]}`;
};

/** A UTC time from the API, which has sometimes come without its "Z". */
export const asUtc = (value: string) => (/(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`);

const inSalon = (instant: string) => dayjs(asUtc(instant)).tz(STORE_TIMEZONE);

export const bookedMinutes = (s: Pick<BookedSummary, 'startUtc' | 'endUtc'>) =>
  s.endUtc ? Math.max(0, dayjs(asUtc(s.endUtc)).diff(dayjs(asUtc(s.startUtc)), 'minute')) : 0;

/** "Wed 14 Oct · 9:30 – 11:45 am", on the salon's clock. */
export const bookedWhen = (s: Pick<BookedSummary, 'startUtc' | 'endUtc'>) => {
  const start = inSalon(s.startUtc);
  return whenRange(start.format('YYYY-MM-DD'), start.format('h:mm A'), bookedMinutes(s));
};

/** "9:30 am", on the salon's clock (a pick-up's first stop, SF 113). */
export const bookedClock = (s: Pick<BookedSummary, 'startUtc'>) => inSalon(s.startUtc).format('h:mm a');

/**
 * A pick-up's collection (mpgadmin D117, 27 Oct 2026: the booking's start is
 * the groom, so we collect before it): "about 1:45 pm", or null when the
 * server gave no estimate (a typed fee, a distance band).
 */
/** "Thu 29 Oct, about 1:45 pm" for a pick-up with an estimate; else the booking's start. */
export const collectOrStart = (s: Pick<BookedSummary, 'startUtc'> & { where?: BookedWhere | null }) =>
  s.where?.visitType === 1 && s.where.collectUtc ? inSalon(s.where.collectUtc).format('ddd D MMM, [about] h:mm a') : bookedStart(s);

export const collectClock = (w: Pick<BookedWhere, 'collectUtc'> | null | undefined) =>
  w?.collectUtc ? `about ${inSalon(w.collectUtc).format('h:mm a')}` : null;

/** "Wed 14 Oct, 9:30 am", on the salon's clock. */
export const bookedStart = (s: Pick<BookedSummary, 'startUtc'>) => inSalon(s.startUtc).format('ddd D MMM, h:mm a');

/** "Leo & Milo · Wed 14 Oct at 9:30 am · in 3 days" (SF 75's line under the title). */
export const bookedHeadline = (s: Pick<BookedSummary, 'startUtc' | 'pets'> & { where?: BookedWhere | null }, now: number) => {
  const start = inSalon(s.startUtc);
  // A pick-up: "Leo · we collect Fri 23 Oct about 8:15 am" (SF 113); the
  // collection is before the groom's start (D117, 27 Oct 2026).
  if (s.where?.visitType === 1) {
    const collect = s.where.collectUtc ? inSalon(s.where.collectUtc) : null;
    return `${joinPets(s.pets.map(p => p.name)) || 'Your pet'} · we collect ${collect ? collect.format('ddd D MMM [about] h:mm a') : start.format('ddd D MMM [at] h:mm a')}`;
  }
  const away = inDays(start.format('YYYY-MM-DD'), now);
  return [joinPets(s.pets.map(p => p.name)), start.format('ddd D MMM [at] h:mm a'), away].filter(Boolean).join(' · ');
};

// ── where a summary comes from ──────────────────────────────────────────

export interface PaymentSummaryLike {
  appointmentId: number;
  startTime: string;
  endTime: string;
  storeName: string;
  storeAddress: string | null;
  pets: {
    petName: string;
    services: {
      serviceName: string;
      optionName: string | null;
      priceCents: number;
      coveredByPack?: boolean;
      /** The line's service deal as the server stored it (mpgadmin G138, #1525); optional. */
      dealTitle?: string | null;
      /** What that deal took off `priceCents`. */
      dealCents?: number;
    }[];
  }[];
  /** The whole-booking deal (mpgadmin #1525): absent or 0 when none or held back. */
  promotionTitle?: string | null;
  promotionCents?: number;
  totalCents: number;
  depositPaidCents: number;
  balanceCents: number;
  paymentStatus: string;
  clientMaskedMobile: string | null;
}

const serviceText = (name: string, option: string | null | undefined, pack: boolean | undefined) =>
  `${name}${option ? ` (${option})` : ''}${pack ? ' · package session' : ''}`;

/**
 * The server's payment summary, the money's truth after Stripe. What only
 * the review knew (the groomer as the client chose it, their mobile, the
 * deposit an unpaid booking owes) comes from the copy it kept, when it is
 * this booking's.
 */
/** A deal's words as the server wrote them, or '' (mpgadmin G138). */
const title = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/**
 * One pet's row with its service deals as the server stored them: the price
 * after them, the price before struck, and each deal by its title. The site
 * works nothing out (G9); it only adds up the server's amounts.
 */
const withDeals = (petName: string, services: string, fullCents: number, deals: { title: string; cents: number }[]): BookedLine => {
  const kept = deals.filter(d => d.title && d.cents > 0);
  const off = kept.reduce((n, d) => n + d.cents, 0);
  return { petName, services, priceCents: fullCents - off, ...(kept.length ? { wasCents: fullCents, deals: kept } : {}) };
};

/**
 * The server's promotion row, and what is still between the lines and the
 * total once it and the line deals are off (more than a cent): with no named
 * promotion that gap is the promotion row, named `fallbackName`; beside one it
 * is a "Discount" row, so the card always adds up to the server's total.
 */
const promotionRows = (
  linesCents: number,
  totalCents: number,
  server: { name: string; amountCents: number } | null,
  fallbackName: string,
): Pick<BookedSummary, 'promotion' | 'deductions'> => {
  const gap = linesCents - (server?.amountCents ?? 0) - totalCents;
  if (!server) return { promotion: gap > 1 ? { name: fallbackName, amountCents: gap } : null };
  return { promotion: server, ...(gap > 1 ? { deductions: [{ name: 'Discount', amountCents: gap }] } : {}) };
};

/**
 * The server's payment summary, the money's truth after Stripe. What only
 * the review knew (the groomer as the client chose it, their mobile, the
 * deposit an unpaid booking owes) comes from the copy it kept, when it is
 * this booking's.
 */
export const fromPaymentSummary = (s: PaymentSummaryLike, kept?: BookedSummary | null): BookedSummary => {
  const mine = kept && kept.appointmentId === s.appointmentId ? kept : null;
  const paid = s.paymentStatus === 'paid' || s.paymentStatus === 'no_payment_required';
  const depositCents = paid ? s.depositPaidCents : mine?.depositCents ?? 0;
  // An API with the deals (mpgadmin #1525) names each line's and the
  // booking's; one without leaves today's single gap row.
  const named =
    s.promotionTitle !== undefined ||
    s.promotionCents !== undefined ||
    s.pets.some(p => p.services.some(sv => sv.dealTitle !== undefined || sv.dealCents !== undefined));
  const petLines: BookedLine[] = s.pets.map(p => {
    const before = p.services.reduce((n, sv) => n + (sv.coveredByPack ? 0 : sv.priceCents), 0);
    return {
      ...withDeals(
      p.petName,
      p.services.map(sv => serviceText(sv.serviceName, sv.optionName, sv.coveredByPack)).join(', '),
      p.services.reduce((n, sv) => n + (sv.coveredByPack ? 0 : sv.priceCents), 0),
      // A session a pack pays for is $0 already: nothing more comes off it.
      p.services.filter(sv => !sv.coveredByPack).map(sv => ({ title: title(sv.dealTitle), cents: Math.round(sv.dealCents ?? 0) })),
      ),
      // "2nd cat price" (mpgadmin G154): the summary doesn't name it, the review did.
      ...keptPlace(mine, p.petName, before),
    };
  });
  const serverPromo =
    named && (s.promotionCents ?? 0) > 0
      ? { name: title(s.promotionTitle) || mine?.promotion?.name || 'Promotion', amountCents: s.promotionCents ?? 0 }
      : null;
  // The server lists each service at its price before the booking's promotion
  // and gives the total after it (the promotion is the booking's, not a
  // line's), so the gap between them is the promotion; drawn without it, $95
  // read as an $80 total (Lena's phone test, 12 Oct). Packages bought with the
  // booking aren't in its lines: the review's own rows for them are added when
  // they make the numbers meet exactly.
  const sum = (lines: BookedLine[]) => lines.reduce((n, l) => n + l.priceCents, 0);
  const extras = mine ? mine.lines.filter(l => !l.petName) : [];
  const keptOff = serverPromo?.amountCents ?? mine?.promotion?.amountCents ?? 0;
  const lines = extras.length > 0 && sum(petLines) + sum(extras) - keptOff === s.totalCents ? [...petLines, ...extras] : petLines;
  const gap = sum(lines) - s.totalCents;
  const rows = named
    ? promotionRows(sum(lines), s.totalCents, serverPromo, mine?.promotion?.name || 'Promotion')
    : { promotion: gap > 0 ? { name: mine?.promotion?.name || 'Promotion', amountCents: gap } : null };
  return {
    appointmentId: s.appointmentId,
    pets: s.pets.map(p => ({ name: p.petName, id: mine?.pets.find(k => k.name === p.petName)?.id })),
    startUtc: asUtc(s.startTime),
    endUtc: s.endTime ? asUtc(s.endTime) : null,
    groomer: mine?.groomer ?? null,
    storeName: s.storeName || mine?.storeName || '',
    storeAddress: s.storeAddress ?? mine?.storeAddress ?? null,
    lines,
    ...rows,
    totalCents: s.totalCents,
    depositCents,
    depositPaid: paid && depositCents > 0,
    balanceCents: paid ? s.balanceCents : Math.max(0, s.totalCents - depositCents),
    mobile: mine?.mobile ?? s.clientMaskedMobile ?? null,
    pickup: mine?.pickup ?? null,
    // The visit (F16): the payment summary is read by the Stripe session alone,
    // so it carries no address (it names no client); the review's copy does.
    // Without it a home visit read as "at the salon" after the deposit.
    where: mine?.where ?? null,
  };
};

export interface ReviewLine {
  petId?: number | string;
  petName: string;
  name: string;
  optionName?: string | null;
  /** Dollars, as the cart keeps them. */
  price: number;
  usePackSession?: boolean;
  /** "2nd cat price" (mpgadmin G154), the quote's words, and the 1st pet's price struck beside it. */
  placeLabel?: string | null;
  fullPrice?: number | null;
}

export interface ReviewFigures {
  appointmentId: number;
  pets: { id?: number; name: string }[];
  startUtc: string;
  minutes: number;
  groomer: string | null;
  storeName: string;
  storeAddress: string | null;
  lines: ReviewLine[];
  /** Packages bought with the booking: their own lines, in the total. */
  extras?: { label: string; price: number }[];
  /** The service deals (mpgadmin G138), each by the server's tag: off the lines, before the promotion. */
  deals?: { name: string; amount: number }[];
  promotion: { name: string; amount: number } | null;
  /** Off at the salon if paid its way (SF 99): "Pay that in cash or by bank transfer". */
  atSalon?: { label: string; amount: number } | null;
  /** Dollars, after the deals and the promotion. */
  total: number;
  deposit: number;
  balance: number;
  mobile: string | null;
  pickup?: { name: string | null; mobile: string } | null;
  photoFailed?: boolean;
  /** The travel fee in the total, in dollars (0 at the salon). */
  travel?: number;
  where?: BookedWhere | null;
}

const cents = (dollars: number) => Math.round((dollars || 0) * 100);

/**
 * A pet's row's multi-pet place (mpgadmin G154), from lines that say it: the
 * server's words and the 1st pet's price for the row, struck. Nothing for a
 * row with no line priced by place.
 */
interface PlaceRow {
  placeLabel?: string | null;
  /** The 1st pet's price for the line; null when not known. */
  fullCents?: number | null;
  priceCents: number;
  pack?: boolean;
}

export const placeOf = (rows: PlaceRow[], priceCents: number): Pick<BookedLine, 'placeLabel' | 'fullCents'> => {
  const placed = rows.find(r => !r.pack && r.placeLabel?.trim());
  if (!placed) return {};
  const known = rows.every(r => r.pack || !r.placeLabel?.trim() || typeof r.fullCents === 'number');
  const full = rows.reduce((n, r) => n + (r.pack ? 0 : r.placeLabel?.trim() && typeof r.fullCents === 'number' ? r.fullCents : r.priceCents), 0);
  return { placeLabel: (placed.placeLabel as string).trim(), ...(known && full > priceCents ? { fullCents: full } : {}) };
};

/**
 * A row the review kept for this pet carries the place's words: the server's
 * payment summary doesn't (yet). The booking read says them itself
 * (`fromAppointmentRead`), so this is only for the summary after Stripe.
 */
const keptPlace = (mine: BookedSummary | null, petName: string, priceCents: number): Pick<BookedLine, 'placeLabel' | 'fullCents'> => {
  const kept = mine?.lines.find(l => l.petName === petName && l.placeLabel);
  // Only when the server's price for the row is the one the review showed.
  return kept && kept.priceCents === priceCents ? { placeLabel: kept.placeLabel, ...(kept.fullCents ? { fullCents: kept.fullCents } : {}) } : {};
};

/** What Review and book showed, for the booked page that follows it. */
export const fromReview = (r: ReviewFigures): BookedSummary => {
  const byPet = new Map<string, ReviewLine[]>();
  r.lines.forEach(line => {
    const key = String(line.petId ?? line.petName);
    byPet.set(key, [...(byPet.get(key) ?? []), line]);
  });
  return {
    appointmentId: r.appointmentId,
    pets: r.pets,
    startUtc: r.startUtc,
    endUtc: r.minutes > 0 ? dayjs(r.startUtc).add(r.minutes, 'minute').toISOString() : null,
    groomer: r.groomer,
    storeName: r.storeName,
    storeAddress: r.storeAddress,
    lines: [
      ...Array.from(byPet.values()).map(list => {
        const priceCents = list.reduce((n, sv) => n + (sv.usePackSession ? 0 : cents(sv.price)), 0);
        return {
          petName: list[0].petName,
          services: list.map(sv => serviceText(sv.name, sv.optionName, sv.usePackSession)).join(', '),
          priceCents,
          ...placeOf(
            list.map(sv => ({ placeLabel: sv.placeLabel, fullCents: sv.fullPrice != null ? cents(sv.fullPrice) : null, priceCents: cents(sv.price), pack: !!sv.usePackSession })),
            priceCents,
          ),
        };
      }),
      ...(r.extras ?? []).map(x => ({ petName: '', services: x.label, priceCents: cents(x.price) })),
      // The drive, after the services, as Review showed it (SF 108).
      ...(r.where && r.travel
        ? [{ petName: '', services: r.where.driveMinutes ? `Travel · about ${Math.round(r.where.driveMinutes)} min` : 'Travel', priceCents: cents(r.travel), ...(r.where.driveMinutes ? { google: true } : {}) }]
        : []),
    ],
    ...(r.deals?.length ? { deductions: r.deals.map(d => ({ name: d.name, amountCents: cents(d.amount) })) } : {}),
    promotion: r.promotion && r.promotion.amount > 0 ? { name: r.promotion.name, amountCents: cents(r.promotion.amount) } : null,
    ...(r.atSalon && r.atSalon.amount > 0 ? { atSalon: { label: r.atSalon.label, amountCents: cents(r.atSalon.amount) } } : {}),
    totalCents: cents(r.total),
    depositCents: cents(r.deposit),
    depositPaid: false,
    balanceCents: cents(r.balance),
    mobile: r.mobile,
    pickup: r.pickup ?? null,
    ...(r.where ? { where: r.where } : {}),
    photoFailed: r.photoFailed || undefined,
  };
};

/**
 * The booking read back by id (a refresh with nothing kept). Its date and
 * time are the stored UTC ones, written without a zone.
 */
export const fromAppointmentRead = (data: any, fallbackId: number): BookedSummary | null => {
  if (!data || !data.appointmentDate || !data.appointmentTime) return null;
  const startUtc = asUtc(`${data.appointmentDate}T${String(data.appointmentTime).slice(0, 5)}:00`);
  const groups: [string, any[]][] = Object.entries(data.petServices || {}) as [string, any[]][];
  const ends = groups.flatMap(([, list]) => list.map((sv: any) => sv?.endTime).filter(Boolean)).map((t: string) => asUtc(t));
  const endUtc = ends.length ? ends.reduce((a, b) => (dayjs(a).isAfter(dayjs(b)) ? a : b)) : null;
  const totalCents = cents(Number(data.totalValue) || 0);
  const paid = !!data.deposit?.isPaid;
  const depositCents = cents(Number(data.deposit?.amount) || 0);
  // Each line at its price at booking, with its service deal as the server
  // stored it, and the whole-booking deal by its title (mpgadmin G138, #1525).
  const lines = groups.map(([, list]) =>
    withDeals(
      list[0]?.petName || 'Your pet',
      list.map((sv: any) => serviceText(sv.serviceName || 'Service', sv.pricingOptionName, false)).join(', '),
      list.reduce((n: number, sv: any) => n + cents(Number(sv.price) || 0), 0),
      list.map((sv: any) => ({ title: title(sv.dealTitle), cents: cents(Number(sv.dealDiscount) || 0) })),
    ),
  ).map((line, i) => {
    // The line's multi-pet place as the server booked it (mpgadmin G154): its
    // words ("2nd cat price") and the option's 1st-pet price, struck beside it.
    // A read from an API before them carries neither, and the row reads as a
    // plain line: nothing here works a place out (G9).
    const list = groups[i][1];
    const placed = placeOf(
      list.map((sv: any) => ({
        placeLabel: sv?.multiPetPlace != null || sv?.multiPetLabel != null ? title(sv.multiPetLabel) : '',
        fullCents: typeof sv?.fullPrice === 'number' ? cents(sv.fullPrice) : null,
        priceCents: cents(Number(sv.price) || 0),
      })),
      line.wasCents ?? line.priceCents,
    );
    return { ...line, ...placed };
  });
  const promoCents = cents(Number(data.promotionDiscount) || 0);
  const serverPromo = promoCents > 0 ? { name: title(data.promotionTitle) || 'Promotion', amountCents: promoCents } : null;
  return {
    appointmentId: Number(data.id) || fallbackId,
    pets: groups.map(([petId, list]) => ({ id: Number(petId) || undefined, name: list[0]?.petName || 'Your pet' })),
    startUtc,
    endUtc,
    groomer: null,
    storeName: data.storeName || '',
    storeAddress: data.storeAddress || null,
    lines,
    ...promotionRows(
      lines.reduce((n, l) => n + l.priceCents, 0),
      totalCents,
      serverPromo,
      'Promotion',
    ),
    totalCents,
    depositCents,
    depositPaid: paid && depositCents > 0,
    balanceCents: Math.max(0, totalCents - (paid ? depositCents : 0)),
    mobile: null,
    // The booking's own pickup contact (mpgadmin G105); none when the client collects.
    pickup:
      typeof data.pickupContactMobile === 'string' && data.pickupContactMobile.trim()
        ? {
            name: typeof data.pickupContactName === 'string' && data.pickupContactName.trim() ? data.pickupContactName.trim() : null,
            mobile: data.pickupContactMobile.trim(),
          }
        : null,
  };
};
